/**
 * FloatingCtaContext
 * ------------------
 * Central registry that decides which floating CTA is allowed to render.
 *
 * Two responsibilities:
 *  1. Slot arbitration: multiple components may want the "bottom-bar" slot on
 *     mobile (generic StickyCtaBar, page-specific PropertyDetail bar, housing
 *     MobileBottomBar, etc). Only the highest-priority claim wins.
 *  2. Route policy: on private/admin/auth/checkout routes we hide global
 *     floating CTAs (chatbot, scroll-top, sticky bar) entirely.
 *
 * Components call `useFloatingCtaSlot("bottom-bar", priority)` and receive a
 * boolean telling them whether to render. The hook auto-unregisters on unmount.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

export type FloatingSlot = "bottom-bar" | "chatbot" | "scroll-top";

interface Claim {
  id: string;
  priority: number;
}

interface FloatingCtaContextValue {
  claim: (slot: FloatingSlot, id: string, priority: number) => void;
  release: (slot: FloatingSlot, id: string) => void;
  isWinner: (slot: FloatingSlot, id: string) => boolean;
  isSlotAllowed: (slot: FloatingSlot) => boolean;
}

const FloatingCtaContext = createContext<FloatingCtaContextValue | null>(null);

/** Routes where floating CTAs (chatbot, sticky bar, scroll-top) must NOT render. */
const HIDDEN_PREFIXES = [
  "/admin",
  "/phcrm",
  "/cliente",
  "/auth",
  "/reset-password",
  "/checkout",
  "/carrito/checkout",
];

/** Routes where the generic StickyCtaBar must yield (page has its own CTA). */
const GENERIC_STICKY_BLOCKED_PATTERNS = [
  /^\/servicios\/alquileres-viviendas\/(propiedad|habitacion)\//,
  /^\/[a-z]{2}\/servicios\/alquileres-viviendas\/(propiedad|habitacion)\//,
];

const isPathBlocked = (pathname: string, list: string[]) =>
  list.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export const FloatingCtaProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const [claims, setClaims] = useState<Record<FloatingSlot, Claim[]>>({
    "bottom-bar": [],
    chatbot: [],
    "scroll-top": [],
  });

  const claim = useCallback((slot: FloatingSlot, id: string, priority: number) => {
    setClaims((prev) => {
      const next = prev[slot].filter((c) => c.id !== id);
      next.push({ id, priority });
      return { ...prev, [slot]: next };
    });
  }, []);

  const release = useCallback((slot: FloatingSlot, id: string) => {
    setClaims((prev) => ({
      ...prev,
      [slot]: prev[slot].filter((c) => c.id !== id),
    }));
  }, []);

  const isSlotAllowed = useCallback(
    (slot: FloatingSlot) => {
      if (isPathBlocked(pathname, HIDDEN_PREFIXES)) return false;
      if (slot === "bottom-bar") {
        // Generic sticky is route-blocked; specific page bars decide separately.
        // This flag is consulted by the GENERIC bar; specific bars use their own claim.
      }
      return true;
    },
    [pathname]
  );

  const isWinner = useCallback(
    (slot: FloatingSlot, id: string) => {
      const list = claims[slot];
      if (list.length === 0) return false;
      const top = list.reduce((a, b) => (b.priority > a.priority ? b : a));
      return top.id === id;
    },
    [claims]
  );

  const value = useMemo<FloatingCtaContextValue>(
    () => ({ claim, release, isWinner, isSlotAllowed }),
    [claim, release, isWinner, isSlotAllowed]
  );

  return (
    <FloatingCtaContext.Provider value={value}>{children}</FloatingCtaContext.Provider>
  );
};

const useFloatingCtaContext = () => {
  const ctx = useContext(FloatingCtaContext);
  if (!ctx) {
    // Safe fallback when provider is missing (e.g. tests): never block.
    return null;
  }
  return ctx;
};

/**
 * Claim a floating slot and learn whether this component is the active winner.
 * Higher priority wins. Pass `enabled=false` to release the claim.
 */
export const useFloatingCtaSlot = (
  slot: FloatingSlot,
  priority: number,
  enabled = true
): boolean => {
  const ctx = useFloatingCtaContext();
  const id = useId();

  useEffect(() => {
    if (!ctx || !enabled) return;
    ctx.claim(slot, id, priority);
    return () => ctx.release(slot, id);
  }, [ctx, slot, id, priority, enabled]);

  if (!ctx) return enabled;
  if (!enabled) return false;
  if (!ctx.isSlotAllowed(slot)) return false;
  return ctx.isWinner(slot, id);
};

/** Read-only check used by global widgets (chatbot, scroll-top). */
export const useIsFloatingSlotAllowed = (slot: FloatingSlot): boolean => {
  const ctx = useFloatingCtaContext();
  const { pathname } = useLocation();
  if (!ctx) return !isPathBlocked(pathname, HIDDEN_PREFIXES);
  return ctx.isSlotAllowed(slot);
};

/** Route-level rule: should the GENERIC StickyCtaBar render here at all? */
export const useGenericStickyAllowed = (): boolean => {
  const { pathname } = useLocation();
  if (isPathBlocked(pathname, HIDDEN_PREFIXES)) return false;
  if (GENERIC_STICKY_BLOCKED_PATTERNS.some((re) => re.test(pathname))) return false;
  return true;
};

/** Priority constants for clarity at call sites. */
export const FloatingPriority = {
  GENERIC_STICKY: 10,
  BLOG: 40,
  ZONE: 50,
  RELOCATION: 60,
  SEGUROS: 60,
  HOUSING: 80,
  PROPERTY_DETAIL: 90,
} as const;
